import {
  ReportPreset,
  SMART_FILTER_FIELDS,
  SmartComparison,
  SmartSort,
  smartVisualizations,
  type SmartBuilderCatalogue,
  type SmartDimension,
  type SmartFilterCode,
  type SmartMetricOption,
  type SmartReportArea,
  type SmartReportFilters,
  type SmartReportPeriod,
  type SmartReportRequest,
  type SmartVisualization,
} from '@optiflow/types';

/**
 * The builder's working state and the rules that decide which question comes next.
 *
 * Every rule reads the metric option the server sent: whether the figure is as of now, which
 * breakdowns and filters it has, whether it can be compared or ranked, which charts can draw it.
 * Nothing here knows what a metric means, so the steps cannot drift from the catalogue.
 */
export interface SmartDraft {
  area: SmartReportArea | null;
  metric: SmartMetricOption | null;
  period: SmartReportPeriod;
  comparison: SmartComparison;
  dimension: SmartDimension | null;
  limit: number | null;
  sort: SmartSort;
  /** The choices the user made; a variant's fixed filters are added when the request is built. */
  filters: SmartReportFilters;
  /** The names of the entities chosen in pickers, for the summary only; never sent. */
  filterNames: Partial<Record<keyof SmartReportFilters, string>>;
  /** Null means the recommended chart for the figure and breakdown. */
  visualization: SmartVisualization | null;
}

export const SMART_STEPS = [
  'area',
  'metric',
  'period',
  'breakdown',
  'filters',
  'visual',
  'review',
] as const;
export type SmartStep = (typeof SMART_STEPS)[number];

export function emptyDraft(): SmartDraft {
  return {
    area: null,
    metric: null,
    period: { preset: ReportPreset.THIS_MONTH },
    comparison: SmartComparison.NONE,
    dimension: null,
    limit: null,
    sort: SmartSort.DESC,
    filters: {},
    filterNames: {},
    visualization: null,
  };
}

/** The filters a user chooses for this metric: everything it offers except what its variant fixes. */
export function choosableFilters(metric: SmartMetricOption): SmartFilterCode[] {
  return metric.filters.filter((filter) => {
    const field = SMART_FILTER_FIELDS[filter];
    return filter !== 'OPTICAL_ATTENTION' && !(field in metric.fixedFilters);
  });
}

export function visualizationsFor(metric: SmartMetricOption, dimension: SmartDimension | null) {
  return smartVisualizations(
    dimension,
    dimension !== null && metric.stackable.includes(dimension),
    metric.chartable,
  );
}

/** The steps this draft actually needs; a step with nothing to decide is skipped, not shown empty. */
export function stepsFor(draft: SmartDraft): SmartStep[] {
  const metric = draft.metric;
  if (!metric) return draft.area ? ['area', 'metric'] : ['area'];
  return SMART_STEPS.filter((step) => {
    switch (step) {
      case 'period':
        return !metric.asOfNow;
      case 'breakdown':
        return metric.dimensions.length > 0;
      case 'filters':
        return choosableFilters(metric).length > 0;
      case 'visual':
        return visualizationsFor(metric, draft.dimension).length > 1;
      default:
        return true;
    }
  });
}

/** Chooses a metric and drops every earlier choice the new metric does not offer. */
export function withMetric(draft: SmartDraft, metric: SmartMetricOption): SmartDraft {
  const allowed = new Set(choosableFilters(metric).map((filter) => SMART_FILTER_FIELDS[filter]));
  const filters = Object.fromEntries(
    Object.entries(draft.filters).filter(
      ([key, value]) => value !== undefined && allowed.has(key as never),
    ),
  ) as SmartReportFilters;
  const dimension =
    draft.dimension && metric.dimensions.includes(draft.dimension) ? draft.dimension : null;
  return {
    ...draft,
    area: metric.area,
    metric,
    filters,
    dimension,
    limit: dimension && metric.rankable.includes(dimension) ? draft.limit : null,
    comparison: metric.comparable ? draft.comparison : SmartComparison.NONE,
    visualization: null,
  };
}

export function withDimension(draft: SmartDraft, dimension: SmartDimension | null): SmartDraft {
  const metric = draft.metric;
  return {
    ...draft,
    dimension,
    limit: metric && dimension && metric.rankable.includes(dimension) ? draft.limit : null,
    visualization: null,
  };
}

/** The request exactly as the server expects it. Dates stay the calendar strings the user picked; the server resolves them. */
export function toRequest(draft: SmartDraft): SmartReportRequest | null {
  const metric = draft.metric;
  if (!metric) return null;
  const shapes = visualizationsFor(metric, draft.dimension);
  const visualization =
    draft.visualization && shapes.includes(draft.visualization) ? draft.visualization : shapes[0];
  const period: SmartReportPeriod | null = metric.asOfNow
    ? null
    : draft.period.preset === ReportPreset.CUSTOM
      ? { preset: ReportPreset.CUSTOM, from: draft.period.from, to: draft.period.to }
      : { preset: draft.period.preset };
  return {
    area: metric.area,
    metricCodes: [metric.code],
    dimensions: draft.dimension ? [draft.dimension] : [],
    period,
    comparison: metric.comparable ? draft.comparison : SmartComparison.NONE,
    filters: { ...draft.filters, ...metric.fixedFilters },
    sort: draft.sort,
    limit: draft.dimension && metric.rankable.includes(draft.dimension) ? draft.limit : null,
    visualization: visualization ?? 'KPI',
  };
}

/** Rebuilds a draft from a server-validated request (a quick report or a suggestion), using the catalogue's own option. */
export function fromRequest(
  request: SmartReportRequest,
  catalogue: SmartBuilderCatalogue,
): SmartDraft | null {
  const metrics = catalogue.areas.flatMap((area) => area.metrics);
  const code = request.metricCodes[0];
  const candidates = metrics.filter((metric) => metric.code === code);
  const metric =
    candidates.find(
      (candidate) =>
        candidate.variant !== null &&
        Object.entries(candidate.fixedFilters).every(
          ([key, value]) => request.filters[key as keyof SmartReportFilters] === value,
        ),
    ) ?? candidates.find((candidate) => candidate.variant === null);
  if (!metric) return null;
  const filters = { ...request.filters };
  for (const key of Object.keys(metric.fixedFilters))
    delete filters[key as keyof SmartReportFilters];
  return {
    area: metric.area,
    metric,
    period: request.period ?? { preset: ReportPreset.THIS_MONTH },
    comparison: request.comparison,
    dimension: request.dimensions[0] ?? null,
    limit: request.limit,
    sort: request.sort,
    filters,
    filterNames: {},
    visualization: request.visualization,
  };
}

const DAY = 86_400_000;

/** A custom range the user can be told about before asking the server: both dates, in order, within the limit. The server still decides. */
export function customRangeProblem(
  period: SmartReportPeriod,
  maxDays: number,
): 'missing' | 'order' | 'long' | null {
  if (period.preset !== ReportPreset.CUSTOM) return null;
  if (!period.from || !period.to) return 'missing';
  const from = Date.parse(`${period.from}T00:00:00Z`);
  const to = Date.parse(`${period.to}T00:00:00Z`);
  if (Number.isNaN(from) || Number.isNaN(to)) return 'missing';
  if (to < from) return 'order';
  if ((to - from) / DAY + 1 > maxDays) return 'long';
  return null;
}
